<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractInvoicing',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelCols = ref(6);
  const ux = ref([]);

  const $data = computed(() => ({
    labelCols: labelCols.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'labelCols' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { labelCols, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const item = computed(() => {
    return props.contract.invoicing;
  });
  const billingCycleName = computed(() => {
    return item.value.billing_cycle.split(' ')[1];
  });
  const billingCycleValue = computed(() => {
    return item.value.billing_cycle.split(' ')[0];
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_2095089031469157_emit_epy9wrywf_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_ah8u7mo8o_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_fsoirx43y_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ContractEditor',
      props: {
        target: 'invoicing',
        contract: _.cloneDeep(props.contract),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_7ivcgyra8 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_yxvmm437f =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_7ivcgyra8,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_yxvmm437f],
    event: 'reload',
  });
  const act_2095089031469157_emit_epy9wrywf = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_epy9wrywf_getActionArgs,
  });
  const inst_evh_2095089031469157_close_ah8u7mo8o =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_epy9wrywf,
      event: 'close',
      when: inst_evh_2095089031469157_close_ah8u7mo8o_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_fsoirx43y =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_fsoirx43y_getActionArgs,
      events: [inst_evh_2095089031469157_close_ah8u7mo8o],
    });
  const inst_evh_2095089031469157_clickPreventStop_tnrrm68cb =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_fsoirx43y,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_tnrrm68cb],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_yxvmm437f,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_ah8u7mo8o,
    inst_evh_2095089031469157_clickPreventStop_tnrrm68cb,
    inst_evh_2095089031469157_clickPreventStop,
  };

  const evh_7315092382398562_reload_yxvmm437f = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_yxvmm437f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_ah8u7mo8o = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_ah8u7mo8o.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_tnrrm68cb = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_tnrrm68cb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Contract', 'Financial') }}
          </strong>
          <ItpButton
            :text="gettext('Edit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!readonly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup v-if="loaded">
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Advances')"
          :labelCols="labelCols"
        >
          <ItpText>
            {{ item.rental_fee_advances_initial_months }}
            {{ pgettext('ContractAdvances', 'months initial') }}
            <br />
            {{ item.rental_fee_advances_reserve_months }}
            {{ pgettext('ContractAdvances', 'months reserved') }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Billing currency')"
          :labelCols="labelCols"
        >
          <ItpText>
            {{ item.billing_currency }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Billing frequency')"
          :labelCols="labelCols"
        >
          <ItpText>
            {{ billingCycleValue }} {{ gettext(billingCycleName) }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Notes on invoice')"
          :labelCols="labelCols"
          :helpTooltip="
            pgettext(
              'Contract',
              'Text to append to the invoices generated from this contract.'
            )
          "
          v-if="item.invoice_notes"
        >
          <ItpText>
            {{ item.invoice_notes }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Contract', 'Payment terms')"
          :labelCols="labelCols"
          v-if="item.payment_terms"
        >
          <ItpText>
            {{ pgettext('ContractPaymentTerms', item.payment_terms.type) }},
            {{ item.payment_terms.value }}
          </ItpText>
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
